import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  LiveKitRoom,
  useTracks,
  VideoTrack,
  useLocalParticipant,
  useRoomContext,
} from "@livekit/components-react";
import { Track, VideoPresets, ScreenSharePresets, RoomEvent } from "livekit-client";
import "@livekit/components-styles";
import { Button } from "@/components/ui/button";
import { BroadcastIcon, VideoCameraIcon, VideoCameraSlashIcon, ArrowsClockwiseIcon, MagnifyingGlassPlusIcon } from "@phosphor-icons/react";
import SellerAvatar from "@/components/SellerAvatar";

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

// ─────────────────────────────────────────────────────────────────────────
// Resolution badge — reads the live track's actual dimensions and renders a
// small pill ("4K · Ultra HD", "1080p · HD", etc). Refreshes every 2s so
// adaptive bitrate (simulcast layer changes) is reflected in real time.
// ─────────────────────────────────────────────────────────────────────────
function resolutionLabel(height) {
  if (!height) return null;
  if (height >= 2000) return { label: "4K · Ultra HD", tone: "gold" };
  if (height >= 1300) return { label: "2K · QHD", tone: "gold" };
  if (height >= 1000) return { label: "1080p · HD", tone: "blue" };
  if (height >= 700) return { label: "720p · HD", tone: "blue" };
  return { label: `${height}p`, tone: "zinc" };
}

function QualityBadge({ height, position = "top-right" }) {
  const r = resolutionLabel(height);
  if (!r) return null;
  const palette =
    r.tone === "gold"
      ? "bg-gradient-to-r from-amber-400 to-amber-300 text-amber-950 border-amber-200"
      : r.tone === "blue"
      ? "bg-[#1D78AB]/90 text-white border-white/20"
      : "bg-zinc-900/80 text-zinc-100 border-white/15";
  // Sellers have an "End stream" button at top-3 right-3, so we drop the
  // badge down by ~64px to avoid overlap. Viewers have a clean top-right.
  const pos =
    position === "seller-top-right"
      ? "top-[100px] right-3 md:top-16 md:right-3"
      : "top-3 right-3 md:top-3 md:right-3";
  return (
    <div
      className={`absolute z-20 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black tracking-wider uppercase backdrop-blur shadow-lg border ${palette} ${pos}`}
      data-testid="stream-quality-badge"
      title={`Live resolution: ${r.label}`}
    >
      {/* Active-broadcast indicator — light red pulsing dot. Tells viewers
          the stream is live RIGHT NOW (matches the LIVE pill's dot). */}
      <span
        className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse shadow-[0_0_6px_rgba(248,113,113,0.95)]"
        aria-hidden="true"
        data-testid="stream-quality-active-dot"
      />
      {r.label}
    </div>
  );
}

// Reads the video element's intrinsic resolution (videoHeight) — works for
// BOTH LiveKit `<VideoTrack>` (which uses an underlying <video>) and our
// mesh path (raw <video>). We poll every 2s because simulcast layer
// switches don't fire any standard event.
function useVideoElementHeight(videoElRef, { enabled = true, fallback = 0 } = {}) {
  const [height, setHeight] = useState(fallback);
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => {
      const el = videoElRef.current;
      if (el && el.videoHeight) setHeight(el.videoHeight);
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, [enabled, videoElRef]);
  return height;
}

// ─────────────────────────────────────────────────────────────────────────
// Unified zoom controller. Tries hardware zoom via
// `MediaStreamTrack.applyConstraints({zoom})` first; falls back to
// software zoom (CSS transform: scale on the local <video>) when the
// camera doesn't expose a zoom capability — which is the case on iOS
// Safari, MacBook FaceTime, and most webcams. The result: pinch-to-zoom
// AND the slider work on EVERY mobile device.
//
// The caller wires `softwareScale` onto the <video> element's style
// when `mode === "software"`. Hardware mode leaves the video alone
// because the camera itself is doing the zoom (sharper).
// ─────────────────────────────────────────────────────────────────────────
const SOFTWARE_ZOOM_MIN = 1;
const SOFTWARE_ZOOM_MAX = 6;
const SOFTWARE_ZOOM_STEP = 0.1;

function useZoomController(getTrack, { enabled }) {
  const [hwCaps, setHwCaps] = useState(null); // { min, max, step } | null
  const [value, setValue] = useState(1);

  // Re-detect hardware capabilities whenever the underlying track changes.
  useEffect(() => {
    if (!enabled) {
      setHwCaps(null);
      setValue(1);
      return undefined;
    }
    const detect = () => {
      try {
        const track = getTrack?.();
        if (!track || typeof track.getCapabilities !== "function") {
          setHwCaps(null);
          return;
        }
        const c = track.getCapabilities();
        if (c && typeof c.zoom === "object" && c.zoom.max > c.zoom.min) {
          setHwCaps({ min: c.zoom.min, max: c.zoom.max, step: c.zoom.step || 0.1 });
          const s = track.getSettings?.();
          if (s && typeof s.zoom === "number") setValue(s.zoom);
        } else {
          setHwCaps(null);
        }
      } catch {
        setHwCaps(null);
      }
    };
    detect();
    const t1 = setTimeout(detect, 500);
    const t2 = setTimeout(detect, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [enabled, getTrack]);

  const mode = hwCaps ? "hardware" : "software";
  const caps = hwCaps || { min: SOFTWARE_ZOOM_MIN, max: SOFTWARE_ZOOM_MAX, step: SOFTWARE_ZOOM_STEP };

  const apply = useCallback(async (next) => {
    let n = Number(next);
    if (!Number.isFinite(n)) return;
    if (n < caps.min) n = caps.min;
    if (n > caps.max) n = caps.max;
    setValue(n);
    if (hwCaps) {
      try {
        const track = getTrack?.();
        if (track) await track.applyConstraints({ advanced: [{ zoom: n }] });
      } catch (e) {
        console.warn("hardware zoom failed, will continue with software fallback", e);
      }
    }
    // Software mode: setValue alone updates the CSS transform via the
    // video element's style binding in the parent component.
  }, [hwCaps, getTrack, caps.min, caps.max]);

  // CSS transform value the parent applies to the <video> element when
  // running in software mode. In hardware mode this is a no-op (scale 1).
  const softwareScale = mode === "software" ? value : 1;

  return { mode, caps, value, apply, softwareScale };
}

function SellerZoomSlider({ controller, className = "" }) {
  const { mode, caps, value, apply } = controller;
  const pct = ((value - caps.min) / (caps.max - caps.min)) * 100;
  // User-requested preset stops: 1× (always), 2×, 4×, 6× — clamped to
  // whatever the active camera actually supports (caps.max).
  const presets = (() => {
    const set = new Set();
    set.add(1);
    [2, 4, 6].forEach((p) => {
      if (p <= caps.max) set.add(p);
    });
    return Array.from(set).sort((a, b) => a - b);
  })();
  const tol = (caps.max - caps.min) * 0.05;
  return (
    <div
      className={`pointer-events-auto inline-flex items-center gap-2 rounded-full bg-zinc-950/75 backdrop-blur-md border border-white/15 px-3 py-1.5 shadow-2xl ${className}`}
      data-testid="seller-zoom-slider"
      data-zoom-mode={mode}
    >
      <MagnifyingGlassPlusIcon size={14} weight="bold" className="text-white/90" />
      <input
        type="range"
        min={caps.min}
        max={caps.max}
        step={caps.step}
        value={value}
        onChange={(e) => apply(parseFloat(e.target.value))}
        className="h-1 w-20 sm:w-28 accent-amber-300 cursor-pointer"
        aria-label="Camera zoom"
        data-testid="seller-zoom-range"
      />
      <div className="flex items-center gap-0.5" data-testid="seller-zoom-presets">
        {presets.map((p) => {
          const active = Math.abs(value - p) <= tol;
          return (
            <button
              key={p}
              type="button"
              onClick={() => apply(p)}
              className={`h-7 min-w-[34px] px-1.5 rounded-full text-[10px] font-black tracking-wider tabular-nums transition-colors border ${
                active
                  ? "bg-amber-300 text-amber-950 border-amber-200"
                  : "bg-white/10 text-white/85 border-white/15 hover:bg-white/20"
              }`}
              aria-label={`Zoom to ${p}x`}
              aria-pressed={active}
              data-testid={`seller-zoom-preset-${p}`}
              title={`${p}x`}
            >
              {p}x
            </button>
          );
        })}
      </div>
      <span className="sr-only">{Math.round(pct)}%</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Pinch-to-zoom on the seller's local video. Two-finger pinch drives the
// SAME `controller.apply()` call as the slider — so hardware zoom (when
// supported) AND software CSS-transform zoom (the fallback for iOS Safari
// + webcams) both respond to pinch gestures. Listeners attach to
// `window` because the LiveTapLayer overlay may intercept touches at the
// video container level — touch events bubble to window regardless.
// Single-finger touches pass through (so double-tap-to-heart still works).
// ─────────────────────────────────────────────────────────────────────────
function useSellerPinchZoom(controller, { enabled }) {
  const ref = useRef({ value: 1, caps: null, apply: null });
  ref.current.value = controller.value;
  ref.current.caps = controller.caps;
  ref.current.apply = controller.apply;

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    let startDist = 0;
    let startZoom = 1;
    let active = false;

    const dist = (a, b) => {
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 2) {
        active = false;
        return;
      }
      startDist = dist(e.touches[0], e.touches[1]);
      startZoom = ref.current.value || 1;
      active = true;
    };

    const onTouchMove = (e) => {
      if (!active || e.touches.length !== 2 || !startDist) return;
      // Prevent page-level pinch-zoom only while we're actively driving the
      // camera — single-finger interactions remain untouched.
      if (e.cancelable) e.preventDefault();
      const d = dist(e.touches[0], e.touches[1]);
      const ratio = d / startDist;
      const caps = ref.current.caps || { min: 1, max: 6 };
      let next = startZoom * ratio;
      if (next < caps.min) next = caps.min;
      if (next > caps.max) next = caps.max;
      const apply = ref.current.apply;
      if (apply) apply(next);
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) {
        startDist = 0;
        active = false;
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled]);
}

/**
 * Viewer-side digital pinch-zoom on the remote video. The seller publishes
 * at 4K so a 1×–4× CSS transform crop preserves crystal clarity (we never
 * upscale beyond the source). Pinch is bound to the video element only,
 * so it doesn't fight the rest of the page UI like the seller hook does.
 *
 * Returns a `style` object the caller spreads onto the <video>.
 */
function useViewerDigitalZoom(videoRef, { enabled }) {
  const [zoom, setZoom] = useState(1);
  const startRef = useRef({ dist: 0, zoom: 1, active: false });
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const el = videoRef.current;
    if (!el) return undefined;
    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const onStart = (e) => {
      if (e.touches.length !== 2) return;
      startRef.current = {
        dist: dist(e.touches[0], e.touches[1]),
        zoom,
        active: true,
      };
    };
    const onMove = (e) => {
      const s = startRef.current;
      if (!s.active || e.touches.length !== 2 || !s.dist) return;
      if (e.cancelable) e.preventDefault();
      const d = dist(e.touches[0], e.touches[1]);
      let next = s.zoom * (d / s.dist);
      if (next < 1) next = 1;
      if (next > 4) next = 4;
      setZoom(next);
    };
    const onEnd = (e) => {
      if (e.touches.length < 2) {
        startRef.current.active = false;
        startRef.current.dist = 0;
      }
    };
    // Double-tap to reset zoom to 1×.
    let lastTap = 0;
    const onTap = () => {
      const now = Date.now();
      if (now - lastTap < 280 && zoom !== 1) {
        setZoom(1);
        lastTap = 0;
      } else {
        lastTap = now;
      }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    el.addEventListener("touchend", onTap, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
      el.removeEventListener("touchend", onTap);
    };
  }, [enabled, videoRef, zoom]);
  return zoom;
}



/**
 * Unified live video component. Auto-picks the best available backend:
 *   - LiveKit (managed SFU) when /api/livekit/config returns enabled=true
 *   - WebRTC mesh over our existing stream WebSocket otherwise
 *
 * Props:
 *   streamId      — stream room identifier
 *   isSeller      — boolean, the seller is the sole publisher
 *   wsRef         — ref to the shared WebSocket from LiveStream (mesh path only)
 *   myConnIdRef   — ref that holds our WS connection id (set on welcome)
 *   isLive        — whether the stream is currently live (vs scheduled)
 *   onBackendChosen — called with "livekit" | "mesh" | "none"
 */
export default function StreamVideo({
  streamId,
  isSeller,
  wsRef,
  myConnIdRef,
  isLive,
  onBackendChosen,
  onCameraOn,
  sellerName,
  sellerPicture,
}) {
  const [mode, setMode] = useState("loading"); // loading | livekit | mesh | error
  const [lkToken, setLkToken] = useState(null);
  const [lkUrl, setLkUrl] = useState(null);
  const [attempt, setAttempt] = useState(0); // bumps to force a re-try

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api.get("/livekit/config");
        if (!cfg.data.enabled) {
          if (!cancelled) {
            setMode("mesh");
            onBackendChosen?.("mesh");
          }
          return;
        }
        // LiveKit is enabled server-side — KEEP retrying the token call.
        // Falling back to mesh here would put viewers on a different
        // backend than the seller (the seller publishes to LiveKit, the
        // viewer listens on the mesh WebSocket) → viewers stuck forever
        // on "Waiting for camera". We retry with exponential backoff up
        // to ~10s and only THEN surface a clear retry button so the
        // viewer can decide if they want to keep waiting.
        const delays = [0, 400, 800, 1600, 3200, 5000];
        let lastErr = null;
        for (let i = 0; i < delays.length; i++) {
          if (cancelled) return;
          if (delays[i] > 0) {
            await new Promise((r) => setTimeout(r, delays[i]));
          }
          try {
            const t = await api.post(`/livekit/token/${streamId}`, {});
            if (cancelled) return;
            setLkToken(t.data.token);
            setLkUrl(t.data.url);
            setMode("livekit");
            onBackendChosen?.("livekit");
            return;
          } catch (e) {
            lastErr = e;
          }
        }
        if (cancelled) return;
        // Token failed every retry. DO NOT silently fall back to mesh —
        // that would leave the viewer staring at a black screen while the
        // seller is broadcasting on LiveKit. Surface the failure so the
        // user can retry manually.
        // eslint-disable-next-line no-console
        console.warn("[stream] livekit token failed after retries", lastErr);
        setMode("error");
      } catch (e) {
        if (!cancelled) {
          // /livekit/config itself failed (likely network) — assume the
          // mesh fallback is intended here, same as if LiveKit were
          // server-side disabled.
          setMode("mesh");
          onBackendChosen?.("mesh");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [streamId, onBackendChosen, attempt]);

  if (mode === "loading") {
    return <VideoFrame><span className="text-zinc-400 text-sm">Preparing stream…</span></VideoFrame>;
  }

  if (mode === "error") {
    return (
      <VideoFrame>
        <div className="flex flex-col items-center gap-3 text-center px-6">
          <p className="text-zinc-300 text-sm">Trouble connecting to the live stream.</p>
          <Button
            size="sm"
            onClick={() => { setMode("loading"); setAttempt((n) => n + 1); }}
            data-testid="stream-video-retry"
          >
            Reconnect
          </Button>
        </div>
      </VideoFrame>
    );
  }

  if (mode === "livekit" && lkToken && lkUrl) {
    return (
      <LiveKitView
        streamId={streamId}
        token={lkToken}
        url={lkUrl}
        isSeller={isSeller}
        isLive={isLive}
        onCameraOn={onCameraOn}
        sellerName={sellerName}
        sellerPicture={sellerPicture}
      />
    );
  }

  return (
    <MeshView
      streamId={streamId}
      isSeller={isSeller}
      wsRef={wsRef}
      myConnIdRef={myConnIdRef}
      isLive={isLive}
      onCameraOn={onCameraOn}
      sellerName={sellerName}
      sellerPicture={sellerPicture}
    />
  );
}

function VideoFrame({ children }) {
  return (
    <div className="absolute inset-0 bg-zinc-900 grid place-items-center overflow-hidden">
      <BroadcastIcon size={64} className="text-zinc-700 absolute opacity-30" />
      {children}
    </div>
  );
}

/* ---------------- LiveKit ---------------- */
function LiveKitView({ streamId, token, url, isSeller, isLive, onCameraOn, sellerName, sellerPicture }) {
  // Viewer-side auto-reconnect: if after 4 seconds the viewer hasn't
  // received the seller's video track yet (most often a LiveKit
  // subscription race on cold-start), force a remount of <LiveKitRoom>
  // which re-joins the room. Reliably fixes "Waiting for seller…"
  // sticking even though the seller is broadcasting.  4s was tuned
  // against the median LK SFU subscription time (~700ms p50, ~2.5s
  // p95) — anything earlier triggers needless remounts on slow phones,
  // anything later feels like a black screen on entry.
  const [reconnectKey, setReconnectKey] = useState(0);
  const [gotTrack, setGotTrack] = useState(false);
  useEffect(() => {
    if (isSeller || gotTrack || !isLive) return undefined;
    const t = setTimeout(() => {
      if (!gotTrack) setReconnectKey((k) => k + 1);
    }, 4000);
    return () => clearTimeout(t);
  }, [isSeller, gotTrack, isLive, reconnectKey]);

  const handleCameraOn = (on) => {
    if (on) setGotTrack(true);
    onCameraOn?.(on);
  };

  return (
    <div className="absolute inset-0 bg-zinc-900 overflow-hidden">
      <LiveKitRoom
        key={reconnectKey}
        serverUrl={url}
        token={token}
        connect={true}
        // Force subscription to every published track in the room. The
        // default IS autoSubscribe=true, but stating it explicitly here
        // protects against silent surprises if a future LiveKit version
        // changes the default — without this, viewers would land on a
        // "Waiting for camera" screen even when the seller is broadcasting.
        connectOptions={{ autoSubscribe: true }}
        // Camera + mic do NOT auto-publish. Sellers tap the big red "Tap to
        // go live" button below — that's the only reliable way to get
        // getUserMedia permission inside a user gesture (mobile browsers
        // silently deny otherwise).
        video={false}
        audio={false}
        // LiveKit v2 moved per-room capture/publish defaults under
        // `options`. We deliberately let LiveKit pick the simulcast tier
        // mix AND the video codec on its own (VP8/H.264/VP9 negotiated
        // per device) because:
        //   • Explicit "h264" was rejected by some LiveKit cloud regions
        //     → viewers stuck on "Waiting for camera"
        //   • A single explicit simulcast layer at 720p while capturing
        //     1080p caused the publish to silently fail on some Androids
        // We still override the bitrate ceiling and capture resolution so
        // the seller's phone doesn't melt encoding 30 Mbps @ 4K@60fps.
        options={{
          videoCaptureDefaults: {
            // 1080p is the platform/IG/TikTok live default and feels native
            // on phones. Cinema/4K Plus mode still ships true 4K via the
            // separate Mux HLS pipeline.
            resolution: VideoPresets.h1080.resolution,
          },
          publishDefaults: {
            // 3.5 Mbps ceiling. Adaptive bitrate handles weak networks
            // automatically — and on a phone with a hardware encoder
            // (every modern device), CPU + thermals stay well under
            // sustained load.
            videoEncoding: {
              maxBitrate: 3_500_000,
              maxFramerate: 30,
              priority: "high",
            },
            // Prefer framerate drops over resolution drops under bandwidth
            // pressure — same as platform/IG.
            degradationPreference: "maintain-resolution",
            screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
            dtx: true,
            red: true,
          },
        }}
        data-lk-theme="default"
        style={{ height: "100%", width: "100%", position: "relative" }}
      >
        <LiveKitVideos
          isSeller={isSeller}
          isLive={isLive}
          onCameraOn={handleCameraOn}
          sellerName={sellerName}
          sellerPicture={sellerPicture}
        />
      </LiveKitRoom>
    </div>
  );
}

function LiveKitVideos({ isSeller, isLive, onCameraOn, sellerName, sellerPicture }) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: true }
  );
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const [starting, setStarting] = useState(false);
  const [permError, setPermError] = useState(false);

  // Defensive: explicitly subscribe to every track every remote
  // participant publishes. `autoSubscribe: true` SHOULD handle this for
  // us, but on some Capacitor WebView builds (and occasionally on flaky
  // mobile networks) the auto-subscribe hint is dropped on initial
  // connect — leaving viewers staring at "no track" while the seller
  // happily broadcasts. This belt-and-braces handler iterates every
  // remote participant on connect AND on every later `TrackPublished`
  // and force-flips `setSubscribed(true)` on each publication.
  useEffect(() => {
    if (!room) return undefined;
    const subscribeAll = () => {
      try {
        room.remoteParticipants?.forEach?.((p) => {
          const pubs = p.trackPublications || p.tracks;
          if (!pubs?.forEach) return;
          pubs.forEach((pub) => {
            if (pub && !pub.isSubscribed && typeof pub.setSubscribed === "function") {
              try { pub.setSubscribed(true); } catch (_) { /* ignore */ }
            }
          });
        });
      } catch (_) { /* ignore */ }
    };
    // On initial mount: try immediately + a couple of follow-up passes
    // (race conditions on initial connect can mean participants exist
    // a tick before their publications hydrate).
    subscribeAll();
    const tNow = setTimeout(subscribeAll, 400);
    const tLater = setTimeout(subscribeAll, 1500);

    const onPublished = () => subscribeAll();
    const onConnected = () => subscribeAll();
    const onParticipantConnected = () => subscribeAll();
    room.on(RoomEvent.TrackPublished, onPublished);
    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
    return () => {
      clearTimeout(tNow);
      clearTimeout(tLater);
      room.off(RoomEvent.TrackPublished, onPublished);
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
    };
  }, [room]);

  // Camera facing mode — defaults to rear ("environment") because that's the
  // camera most live-shopping streamers use to show items. Tap the flip button
  // to toggle to the selfie ("user") camera.
  const [facing, setFacing] = useState("environment");
  const [flipping, setFlipping] = useState(false);

  const pubTrack = tracks.find((t) => t.participant && !t.participant.isLocal);
  const localTrack = tracks.find((t) => t.participant && t.participant.isLocal);
  const displayTrack = isSeller ? localTrack : pubTrack;

  // Resolve the underlying <video> element from LiveKit's render so we can
  // poll videoHeight for the resolution badge. The DOM ref isn't directly
  // exposed by <VideoTrack>, but the live <video> always lives inside the
  // current LiveKitRoom div — we grab it via a wrapper ref.
  const videoWrapRef = useRef(null);
  const videoElRef = useRef(null);
  useEffect(() => {
    const wrap = videoWrapRef.current;
    if (!wrap) return undefined;
    const findVideo = () => {
      const v = wrap.querySelector("video");
      if (v) videoElRef.current = v;
    };
    findVideo();
    const id = setInterval(findVideo, 1500);
    return () => clearInterval(id);
  }, [displayTrack]);
  const height = useVideoElementHeight(videoElRef, { enabled: !!displayTrack });

  // Expose the local camera's MediaStreamTrack to the zoom controller.
  const getLocalCameraTrack = useCallback(() => {
    try {
      if (!localParticipant) return null;
      const pub = localParticipant.getTrackPublication?.(Track.Source.Camera)
        || (localParticipant.videoTrackPublications &&
          [...localParticipant.videoTrackPublications.values()].find((p) => p.source === Track.Source.Camera));
      const t = pub?.track || pub?.audioTrack || null;
      return t?.mediaStreamTrack || null;
    } catch {
      return null;
    }
  }, [localParticipant]);

  // Unified zoom controller — hardware first, software CSS-transform
  // fallback for iOS Safari + webcams. Slider, presets, and pinch all
  // write to the same `apply()` function.
  const zoomController = useZoomController(getLocalCameraTrack, {
    enabled: isSeller && !!displayTrack,
  });
  // Software-zoom CSS transform on the underlying <video> element. In
  // hardware mode this is `scale(1)` (no-op).
  useEffect(() => {
    const v = videoElRef.current;
    if (!v) return;
    if (zoomController.mode === "software") {
      v.style.transform = `scale(${zoomController.softwareScale})`;
      v.style.transformOrigin = "center center";
      v.style.transition = "transform 0.08s linear";
    } else {
      v.style.transform = "";
      v.style.transformOrigin = "";
      v.style.transition = "";
    }
  }, [zoomController.mode, zoomController.softwareScale, displayTrack]);

  // Pinch-to-zoom on the seller's local video. Single-finger touches pass
  // through so double-tap-to-heart and other gestures still work.
  useSellerPinchZoom(zoomController, {
    enabled: isSeller && !!displayTrack,
  });

  // Notify parent when the camera state changes — used to gate the
  // tap-react overlay so it doesn't block the seller's "Tap to go live"
  // button before their camera is on.
  useEffect(() => {
    onCameraOn?.(!!displayTrack);
  }, [displayTrack, onCameraOn]);

  // Apply softer-light camera constraints AFTER the LiveKit track is up.
  // Ring lights tend to blow out the auto-exposure curve on phone
  // cameras → washed-out skin tones, lost detail in highlights. These
  // constraints walk the AE/AWB down a notch when the device supports
  // them (modern Chrome Android — Samsung, Pixel, OnePlus). Devices
  // that don't support these advanced constraints silently ignore them.
  // Negative `exposureCompensation` = slightly darker exposure.
  // The seller can still fine-tune their light physically; this just
  // makes the broadcast NOT look bleached on default settings.
  const applyRinglightConstraints = async () => {
    try {
      const trackPub = localParticipant?.getTrackPublication?.(Track.Source.Camera)
        || localParticipant?.videoTrackPublications?.values?.().next?.()?.value;
      const mediaTrack = trackPub?.track?.mediaStreamTrack;
      if (!mediaTrack || typeof mediaTrack.applyConstraints !== "function") return;
      await mediaTrack.applyConstraints({
        advanced: [
          { exposureMode: "continuous" },
          { whiteBalanceMode: "continuous" },
          { exposureCompensation: -1.0 },
          { brightness: -10 },
          { contrast: 10 },
        ],
      }).catch(() => { /* unsupported constraints are ignored silently */ });
    } catch (_) {
      /* never block the broadcast on an exposure tweak */
    }
  };

  const startCamera = async () => {
    if (!localParticipant || starting) return;
    setStarting(true);
    setPermError(false);
    try {
      await localParticipant.setCameraEnabled(true, {
        facingMode: facing,
      });
      await localParticipant.setMicrophoneEnabled(true);
      // Best-effort: dampen the over-bright ringlight exposure that
      // washes out the seller's face. Runs after a tiny delay so the
      // track is fully attached when we apply constraints.
      setTimeout(applyRinglightConstraints, 300);
    } catch (e) {
      console.error("LiveKit camera enable failed", e);
      setPermError(true);
    } finally {
      setStarting(false);
    }
  };

  const stopCamera = async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setCameraEnabled(false);
      await localParticipant.setMicrophoneEnabled(false);
    } catch {
      /* ignore */
    }
  };

  // Flip rear ↔ front. Two modes:
  //   • Camera OFF (pre-go-live)  → just toggle the `facing` state. The
  //     next `startCamera` call uses it so the camera comes up on the
  //     right lens. No track work happens here.
  //   • Camera ON  → atomically restart the existing camera track on the
  //     new facingMode using LiveKit's `restartTrack`. This is a single
  //     SID-preserving swap — viewers see a smooth transition with no
  //     reconnect, and the seller's UI flips in one tap (no
  //     disable-then-enable race where the first click is "swallowed").
  const flipCamera = async () => {
    if (!localParticipant || flipping) return;
    const next = facing === "environment" ? "user" : "environment";
    if (!displayTrack) {
      // Camera not yet on — just remember the chosen lens for next start.
      setFacing(next);
      return;
    }
    setFlipping(true);
    try {
      // Optimistically update state so the UI feedback (mirror, label)
      // changes on the same tap.
      setFacing(next);
      // Find the live camera publication on the local participant and
      // restart its underlying MediaStreamTrack with the new facingMode.
      // `restartTrack` keeps the publication / subscription state intact
      // (no signaling round-trip, no re-negotiation).
      const pub = Array.from(localParticipant.videoTrackPublications.values())
        .find((p) => p.source === Track.Source.Camera) ||
        Array.from(localParticipant.trackPublications.values())
          .find((p) => p.kind === "video" && p.source === Track.Source.Camera);
      const camTrack = pub?.track;
      if (camTrack && typeof camTrack.restartTrack === "function") {
        await camTrack.restartTrack({ facingMode: next });
      } else {
        // Fallback for older LiveKit builds: use setCameraEnabled with
        // explicit facingMode override. We DON'T disable first — passing
        // a new options object causes LiveKit to switch the underlying
        // track in place.
        await localParticipant.setCameraEnabled(true, { facingMode: next });
      }
    } catch (e) {
      // Revert the optimistic state if the flip actually failed.
      setFacing(facing);
      console.error("LiveKit flip camera failed", e);
    } finally {
      setFlipping(false);
    }
  };

  // Bridge window events from the right-rail buttons in LiveStream.jsx —
  // the rail can't reach into LiveKit context directly, so we use small
  // custom events as the API surface.
  useEffect(() => {
    const onFlip = () => { flipCamera(); };
    const onEnd  = () => { stopCamera(); };
    window.addEventListener("seller:flip-camera", onFlip);
    window.addEventListener("seller:end-camera", onEnd);
    return () => {
      window.removeEventListener("seller:flip-camera", onFlip);
      window.removeEventListener("seller:end-camera", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localParticipant, facing, flipping]);

  return (
    <>
      {displayTrack ? (
        <>
          <div
            ref={videoWrapRef}
            style={{ position: "absolute", inset: 0 }}
          >
            <VideoTrack
              trackRef={displayTrack}
              style={{ height: "100%", width: "100%", objectFit: "cover" }}
            />
          </div>
          {isSeller && (
            <>
              {/* Camera zoom — slider stays in sync with pinch gesture (both
                  write to the same MediaStreamTrack zoom constraint). On
                  mobile, pinned above the active listing card and bid bar
                  so it never overlaps. On desktop, sits bottom-left near
                  the existing flip/end controls. Hidden when the active
                  camera doesn't support optical zoom. */}
              {/* Zoom slider removed per seller request — use pinch-to-zoom
                  on the video surface instead. The `usePinchZoom` hook is
                  still attached to the seller's <video>, so two-finger
                  pinch drives the same MediaStreamTrack / CSS-transform
                  zoom pipeline. */}
              {/* Desktop only — on mobile the same actions live in the
                 right rail (Flip / End video). */}
              <div className="hidden md:flex absolute bottom-3 left-3 gap-2 z-10">
                <button
                  type="button"
                  onClick={flipCamera}
                  disabled={flipping}
                  className="h-10 w-10 grid place-items-center rounded-full bg-zinc-900/85 backdrop-blur border border-white/15 text-white hover:bg-zinc-800 transition disabled:opacity-50"
                  aria-label={facing === "environment" ? "Switch to selfie camera" : "Switch to rear camera"}
                  title={facing === "environment" ? "Switch to selfie camera" : "Switch to rear camera"}
                  data-testid="seller-flip-camera"
                >
                  <ArrowsClockwiseIcon size={18} weight="bold" />
                </button>
                <Button
                  onClick={stopCamera}
                  size="sm"
                  className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="seller-stop-video"
                >
                  End video
                </Button>
              </div>
            </>
          )}
        </>
      ) : isSeller && isLive ? (
        /* Seller, live, no camera yet — show the same big red CTA as the
           mesh path so the UX is consistent. Their profile picture sits
           behind the button as a clean placeholder instead of dead space. */
        <div
          className="absolute inset-0 grid place-items-center pb-[50%] md:pb-[13%]"
          data-testid="seller-go-live-card"
        >
          {/* Placeholder avatar removed per request — the red Go Live
              button stands alone on a dark stage. */}
          <div className="relative flex flex-col items-center gap-5 text-zinc-100 p-6 text-center">
            <button
              type="button"
              onClick={startCamera}
              disabled={starting}
              className="relative h-28 w-28 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white grid place-items-center shadow-2xl shadow-red-600/40 active:scale-95 transition-transform disabled:opacity-60"
              data-testid="seller-start-video"
              aria-label="Go live"
            >
              <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" />
              <VideoCameraIcon size={42} weight="fill" className="relative" />
            </button>
            <div className="flex flex-col items-center gap-1">
              <p className="font-display font-black text-2xl tracking-tight">
                {permError ? "Tap to retry camera" : starting ? "Starting…" : "Tap to go live"}
              </p>
              {permError && (
                <p className="text-xs text-zinc-300/80 max-w-[280px]">
                  Your browser blocked camera access. Allow it in the address bar, then tap above.
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Viewer (or seller before stream is live).
           During the brief LiveKit subscription warm-up on a LIVE stream
           we paint the seller's profile picture full-bleed (blurred) so
           the viewer feels they're already in the show. When the seller
           hasn't set a picture, we fall back to a branded radial gradient
           in Bidorah's blue + a large initial avatar — so the warm-up
           state still looks intentional and never just "dark void". The
           real video covers it the moment the first frame arrives.
           For non-live states (preparing / scheduled) the friendly "Wait
           for the show" card still shows. */
        isLive ? (
          <>
            <div
              className="absolute inset-0"
              data-testid="stream-warmup-backdrop"
              style={{
                background: sellerPicture
                  ? "#0d2436"
                  : "radial-gradient(ellipse at center, #1D78AB 0%, #0F3D5C 55%, #050F1B 100%)",
                backgroundImage: sellerPicture ? `url("${sellerPicture}")` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: sellerPicture ? "blur(28px) brightness(0.72)" : undefined,
                transform: sellerPicture ? "scale(1.08)" : undefined,
              }}
            />
            {/* Centered seller mark — readable regardless of whether the
                picture exists. Anchors the warm-up moment as "this is the
                show you're tuning into" rather than a featureless screen. */}
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <SellerAvatar
                name={sellerName || "Seller"}
                picture={sellerPicture}
                size={128}
                editable={false}
              />
            </div>
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-center px-6">
            <div className="flex flex-col items-center gap-4">
              <SellerAvatar
                name={sellerName || "Seller"}
                picture={sellerPicture}
                size={96}
                editable={false}
              />
              <div className="text-zinc-300">
                <p className="font-display font-bold text-lg">
                  {sellerName ? `Waiting for ${sellerName}…` : "Waiting for video…"}
                </p>
                <p className="text-xs text-zinc-400 mt-1 max-w-[320px]">
                  {isSeller ? "This stream isn't live yet." : "This stream hasn't started."}
                </p>
              </div>
            </div>
          </div>
        )
      )}
      {/* LIVEKIT · N in room debug pill removed per UX direction. */}
    </>
  );
}

/* ---------------- WebRTC Mesh ---------------- */
function MeshView({ streamId, isSeller, wsRef, myConnIdRef, isLive, onCameraOn, sellerName, sellerPicture }) {
  const [publishing, setPublishing] = useState(false);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [remoteStream, setRemoteStream] = useState(null);
  const [sellerConnId, setSellerConnId] = useState(null);
  const [autoStartFailed, setAutoStartFailed] = useState(false);
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const autoStartTriedRef = useRef(false);

  // Tell the parent when video is on so the tap-react overlay can be gated.
  useEffect(() => {
    onCameraOn?.(isSeller ? !!publishing : !!remoteStream);
  }, [publishing, remoteStream, isSeller, onCameraOn]);
  // Seller: map of viewer_conn_id -> RTCPeerConnection
  // Viewer: single RTCPeerConnection to the seller
  const peersRef = useRef(new Map());
  const viewerPeerRef = useRef(null);
  // Tracks which physical camera is currently in use. Default `environment`
  // (rear) — sellers usually go live with the rear camera pointed at their
  // product, then flip to selfie if they want to be on screen.
  const [facing, setFacing] = useState("environment");

  // ---- Seller: start (or restart) camera with a chosen facingMode ----
  const startPublishing = useCallback(async (facingOverride) => {
    if (!isSeller) return;
    const targetFacing = facingOverride || facing;
    try {
      // If we're already publishing, stop the previous tracks before grabbing
      // a new stream — otherwise the previous camera stays held by the
      // browser and getUserMedia for the other facingMode silently fails.
      if (localStreamRef.current) {
        try { localStreamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
        localStreamRef.current = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          // 1080p @ 30fps with a soft 720p floor. Phones lock to H.264
          // hardware encode at this profile and stay COOL. Requesting 4K
          // @ 60 here (the prior behavior) forced the phone into a
          // software encode path that pegs CPU + drains battery + makes
          // the device painfully hot — and the network can't carry the
          // resulting bitrate anyway, so packets drop and the stream
          // cuts in/out. Cinema/4K Plus mode is the right tool for that.
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30, max: 30 },
          facingMode: { ideal: targetFacing },
          advanced: [
            { width: { min: 1280 }, height: { min: 720 } },
          ],
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 48000 },
          channelCount: { ideal: 2 },
        },
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        const p = localVideoRef.current.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
      setFacing(targetFacing);
      setPublishing(true);
      setAutoStartFailed(false);
      // Republish the new tracks to existing peers so viewers see the flip.
      try {
        for (const pc of peersRef.current.values()) {
          const senders = pc.getSenders();
          stream.getTracks().forEach((track) => {
            const sender = senders.find((s) => s.track && s.track.kind === track.kind);
            if (sender) sender.replaceTrack(track);
          });
        }
      } catch {
        /* peer may not be ready; harmless */
      }
    } catch (e) {
      console.error("Camera access denied", e);
      setAutoStartFailed(true);
    }
  }, [isSeller, facing]);

  // Flip rear ↔ front. When the camera isn't already on we just remember
  // the chosen lens (so the next go-live tap uses it). When it IS on we
  // re-publish with the new facingMode.
  const flipCamera = useCallback(() => {
    const next = facing === "environment" ? "user" : "environment";
    // Commit the new facing immediately so the UI mirror/label updates on
    // the same tap — previously this was set inside `startPublishing` only
    // after the new MediaStream was acquired, which felt like the first
    // click was being swallowed.
    setFacing(next);
    if (!publishing) return;
    startPublishing(next);
  }, [facing, publishing, startPublishing]);

  // Bridge window events from the right-rail buttons in LiveStream.jsx so
  // the rail can drive these camera controls (mobile UX moves them off the
  // video surface and into the right-side icon stack).
  useEffect(() => {
    if (!isSeller) return;
    const onFlip = () => { flipCamera(); };
    const onEnd  = () => { stopPublishingRef.current && stopPublishingRef.current(); };
    window.addEventListener("seller:flip-camera", onFlip);
    window.addEventListener("seller:end-camera", onEnd);
    return () => {
      window.removeEventListener("seller:flip-camera", onFlip);
      window.removeEventListener("seller:end-camera", onEnd);
    };
  }, [isSeller, flipCamera]);
  // We capture stopPublishing into a ref because it's defined below this
  // useEffect block. This dance keeps event-handler identity stable while
  // still letting them call the latest implementation.
  const stopPublishingRef = useRef(null);

  // NOTE: We deliberately do NOT auto-start the camera. The seller must
  // explicitly tap the big red "Tap to go live" button — this respects user
  // intent and reliably grants camera permission within the user gesture
  // (which is the only way mobile browsers will accept getUserMedia).
  // (Previous auto-start useEffect removed for this reason.)

  // Re-bind the local stream to the <video> element whenever the publishing
  // flag flips on. Without this, there's a race: we assign srcObject inside
  // startPublishing() *before* React has re-rendered the <video> tag, so the
  // ref is null and nothing displays. This effect fires after the re-render
  // and guarantees the front-camera feed actually shows up.
  useEffect(() => {
    if (!publishing) return;
    const video = localVideoRef.current;
    const stream = localStreamRef.current;
    if (video && stream && video.srcObject !== stream) {
      video.srcObject = stream;
      // Some mobile browsers require an explicit play() after assigning a
      // MediaStream — autoplay attribute alone isn't always enough.
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [publishing]);

  const stopPublishing = useCallback(() => {
    const s = localStreamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
    }
    localStreamRef.current = null;
    peersRef.current.forEach((pc) => pc.close());
    peersRef.current.clear();
    setPublishing(false);
  }, []);
  // Keep the ref in sync so the window-event listeners above can reach
  // the latest implementation without re-binding.
  useEffect(() => { stopPublishingRef.current = stopPublishing; }, [stopPublishing]);

  const toggleCamera = () => {
    const s = localStreamRef.current;
    if (!s) return;
    const track = s.getVideoTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setCameraOn(track.enabled);
    }
  };
  const toggleMic = () => {
    const s = localStreamRef.current;
    if (!s) return;
    const track = s.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setMicOn(track.enabled);
    }
  };

  // ---- Seller: on viewer request, create a peer connection ----
  const sellerCreatePeerFor = useCallback(
    async (viewerConnId) => {
      const localStream = localStreamRef.current;
      if (!localStream) return;
      if (peersRef.current.has(viewerConnId)) return;
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      peersRef.current.set(viewerConnId, pc);
      // Add tracks. After they're attached, bump the video sender's encoding
      // ceiling so viewers get a sharp HD stream instead of the browser's
      // default ~1 Mbps "safe" bitrate (which is what was making the mesh
      // feed look blurry on the audience side).
      localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));
      try {
        const videoSender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
        if (videoSender) {
          const params = videoSender.getParameters();
          params.degradationPreference = "maintain-resolution";
          params.encodings = (params.encodings && params.encodings.length
            ? params.encodings
            : [{}]).map((enc) => ({
              ...enc,
              maxBitrate: 3_500_000,   // 3.5 Mbps — 1080p that mobile encoders can handle without overheating
              maxFramerate: 30,
              priority: "high",
              networkPriority: "high",
              scaleResolutionDownBy: 1,
            }));
          await videoSender.setParameters(params);
        }
        const audioSender = pc.getSenders().find((s) => s.track && s.track.kind === "audio");
        if (audioSender) {
          const ap = audioSender.getParameters();
          ap.encodings = (ap.encodings && ap.encodings.length ? ap.encodings : [{}]).map((enc) => ({
            ...enc,
            maxBitrate: 128_000,
            priority: "high",
            networkPriority: "high",
          }));
          await audioSender.setParameters(ap);
        }
      } catch (_) { /* older browsers — graceful fallback to defaults */ }
      pc.onicecandidate = (e) => {
        if (e.candidate && wsRef.current?.readyState === 1) {
          wsRef.current.send(
            JSON.stringify({
              type: "webrtc_ice",
              to: viewerConnId,
              candidate: e.candidate,
            })
          );
        }
      };
      pc.onconnectionstatechange = () => {
        if (["failed", "disconnected", "closed"].includes(pc.connectionState)) {
          pc.close();
          peersRef.current.delete(viewerConnId);
          if (pc._adaptiveTimer) {
            clearInterval(pc._adaptiveTimer);
            pc._adaptiveTimer = null;
          }
        }
      };

      // Per-peer adaptive bitrate — mesh equivalent of simulcast. Every 5s
      // we sample this single viewer's getStats() and pick the right encoding
      // tier for THEIR network without affecting the other viewers' peers:
      //
      //   HD  — 3.5 Mbps, 1080p @ 30fps        (good network, default)
      //   MD  — 1.6 Mbps, 720p cap             (mid network)
      //   SD  — 0.6 Mbps, 540p cap @ 24fps     (weak network, e.g. 3G)
      //
      // Switches use a tiny hysteresis band (must stay in the new tier for
      // ~10s before moving) so a one-off RTT spike doesn't flap quality.
      pc._tier = "HD";
      pc._tierSince = Date.now();
      const applyTier = async (tier) => {
        try {
          const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
          if (!sender) return;
          const params = sender.getParameters();
          const cfg = tier === "SD"
            ? { maxBitrate: 600_000, maxFramerate: 24, scaleResolutionDownBy: 2 }
            : tier === "MD"
              ? { maxBitrate: 1_600_000, maxFramerate: 30, scaleResolutionDownBy: 1.5 }
              : { maxBitrate: 3_500_000, maxFramerate: 30, scaleResolutionDownBy: 1 };
          params.encodings = (params.encodings && params.encodings.length ? params.encodings : [{}]).map((enc) => ({
            ...enc,
            ...cfg,
            priority: "high",
            networkPriority: "high",
          }));
          await sender.setParameters(params);
        } catch (_) { /* setParameters can race during ICE; safe to skip */ }
      };
      pc._adaptiveTimer = setInterval(async () => {
        try {
          const stats = await pc.getStats();
          let outboundBitrate = null;
          let packetLossPct = 0;
          stats.forEach((rep) => {
            if (rep.type === "candidate-pair" && rep.state === "succeeded" && rep.availableOutgoingBitrate) {
              outboundBitrate = rep.availableOutgoingBitrate;
            }
            if (rep.type === "remote-inbound-rtp" && rep.kind === "video" && typeof rep.fractionLost === "number") {
              packetLossPct = Math.max(packetLossPct, rep.fractionLost * 100);
            }
          });
          let target = pc._tier;
          if (outboundBitrate != null) {
            if (outboundBitrate < 900_000 || packetLossPct > 6)         target = "SD";
            else if (outboundBitrate < 2_400_000 || packetLossPct > 2)  target = "MD";
            else                                                         target = "HD";
          } else if (packetLossPct > 6) {
            target = "SD";
          }
          // Hysteresis: only switch if the candidate has held for 10s.
          if (target !== pc._tier) {
            const dwell = Date.now() - (pc._candidateSince || 0);
            if (pc._candidate === target && dwell >= 10_000) {
              pc._tier = target;
              pc._tierSince = Date.now();
              await applyTier(target);
            } else if (pc._candidate !== target) {
              pc._candidate = target;
              pc._candidateSince = Date.now();
            }
          } else {
            pc._candidate = null;
          }
        } catch (_) { /* getStats hiccups during teardown — safe to skip */ }
      }, 5000);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(
        JSON.stringify({
          type: "webrtc_offer",
          to: viewerConnId,
          sdp: pc.localDescription,
        })
      );
    },
    [wsRef]
  );

  // ---- Viewer: receive offer, answer ----
  const viewerHandleOffer = useCallback(
    async (fromConnId, sdp) => {
      if (viewerPeerRef.current) {
        try { viewerPeerRef.current.close(); } catch {}
      }
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      viewerPeerRef.current = pc;
      const stream = new MediaStream();
      pc.ontrack = (e) => {
        e.streams[0].getTracks().forEach((t) => stream.addTrack(t));
        setRemoteStream(stream);
      };
      pc.onicecandidate = (e) => {
        if (e.candidate && wsRef.current?.readyState === 1) {
          wsRef.current.send(
            JSON.stringify({
              type: "webrtc_ice",
              to: fromConnId,
              candidate: e.candidate,
            })
          );
        }
      };
      setSellerConnId(fromConnId);
      await pc.setRemoteDescription(sdp);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      wsRef.current?.send(
        JSON.stringify({
          type: "webrtc_answer",
          to: fromConnId,
          sdp: pc.localDescription,
        })
      );
    },
    [wsRef]
  );

  // ---- Seller: receive answer ----
  const sellerHandleAnswer = useCallback(async (viewerConnId, sdp) => {
    const pc = peersRef.current.get(viewerConnId);
    if (!pc) return;
    try {
      await pc.setRemoteDescription(sdp);
    } catch (e) {
      console.error("setRemoteDescription failed", e);
    }
  }, []);

  // ---- both sides: ICE candidate ----
  const handleIce = useCallback(
    async (fromConnId, candidate) => {
      try {
        if (isSeller) {
          const pc = peersRef.current.get(fromConnId);
          if (pc) await pc.addIceCandidate(candidate);
        } else {
          const pc = viewerPeerRef.current;
          if (pc) await pc.addIceCandidate(candidate);
        }
      } catch (e) {
        // ignore — non-fatal, candidate race
      }
    },
    [isSeller]
  );

  // ---- WS message routing (attach onmessage in addition to the chat/bid handler) ----
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    const handler = (evt) => {
      let data;
      try {
        data = JSON.parse(evt.data);
      } catch {
        return;
      }
      // Viewers: when seller joins live, request the stream
      if (!isSeller && data.type === "presence" && data.event === "join" && data.is_seller) {
        setSellerConnId(data.conn_id);
        ws.send(JSON.stringify({ type: "request_stream", to: data.conn_id }));
        return;
      }
      // Viewers: if the seller flips their camera on AFTER we already
      // joined, the seller broadcasts a `request_stream` (with
      // `from_is_seller=true`) to wake every viewer up. Without this
      // re-request, the very first peer connection attempt happened
      // BEFORE the seller had any tracks to publish, so it never
      // produced an offer — and the viewer was stuck on "Waiting for
      // X…" until they manually refreshed. Re-issuing a targeted
      // `request_stream` here makes the viewer pick up the live feed
      // in <1s.
      if (!isSeller && data.type === "request_stream" && data.from && data.from_is_seller) {
        setSellerConnId(data.from);
        ws.send(JSON.stringify({ type: "request_stream", to: data.from }));
        return;
      }
      // Seller: on viewer request, create peer + send offer
      if (isSeller && data.type === "request_stream" && data.from) {
        sellerCreatePeerFor(data.from);
        return;
      }
      // Offer/answer/ice routing
      if (data.type === "webrtc_offer" && !isSeller) {
        viewerHandleOffer(data.from, data.sdp);
      } else if (data.type === "webrtc_answer" && isSeller) {
        sellerHandleAnswer(data.from, data.sdp);
      } else if (data.type === "webrtc_ice") {
        handleIce(data.from, data.candidate);
      }
    };
    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
  }, [wsRef, isSeller, sellerCreatePeerFor, viewerHandleOffer, sellerHandleAnswer, handleIce]);

  // When seller's camera starts, re-announce so viewers already present know to request
  useEffect(() => {
    if (!isSeller || !publishing) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    ws.send(
      JSON.stringify({
        type: "request_stream",
        // broadcast: tells viewers "seller is ready, send me request_stream back"
        // viewers listen for presence already; this is just a nudge
      })
    );
  }, [publishing, isSeller, wsRef]);

  // Viewer: if we joined first and seller is already in room, proactively request
  useEffect(() => {
    if (isSeller) return;
    const ws = wsRef.current;
    if (!ws) return;
    // Send a broadcast request; the seller handles it and responds with an offer
    const kick = () => {
      if (ws.readyState === 1) {
        ws.send(JSON.stringify({ type: "request_stream" }));
      }
    };
    // Short delay so the WS welcome has arrived
    const t = setTimeout(kick, 600);
    return () => clearTimeout(t);
  }, [isSeller, wsRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try { stopPublishing(); } catch {}
      if (viewerPeerRef.current) {
        try { viewerPeerRef.current.close(); } catch {}
        viewerPeerRef.current = null;
      }
    };
  }, [stopPublishing]);

  // Bind remote stream to video element
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Live resolution badge for both seller (their own track) and viewers
  // (the remote track piped over WebRTC mesh).
  const activeVideoRef = isSeller ? localVideoRef : remoteVideoRef;
  const height = useVideoElementHeight(activeVideoRef, {
    enabled: isSeller ? !!publishing : !!remoteStream,
  });

  // Expose the local camera MediaStreamTrack to the zoom controller.
  const getLocalCameraTrack = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return null;
    return s.getVideoTracks()[0] || null;
  }, []);

  // Unified zoom controller (hardware → software fallback).
  const zoomController = useZoomController(getLocalCameraTrack, {
    enabled: isSeller && publishing,
  });
  useEffect(() => {
    const v = localVideoRef.current;
    if (!v) return;
    if (zoomController.mode === "software") {
      v.style.transform = `scale(${zoomController.softwareScale})`;
      v.style.transformOrigin = "center center";
      v.style.transition = "transform 0.08s linear";
    } else {
      v.style.transform = "";
      v.style.transformOrigin = "";
      v.style.transition = "";
    }
  }, [zoomController.mode, zoomController.softwareScale, publishing]);

  // Pinch-to-zoom on the seller's local video container.
  useSellerPinchZoom(zoomController, {
    enabled: isSeller && publishing,
  });

  // Viewer-side digital zoom. The 4K source means a 1×–4× CSS crop stays
  // pixel-perfect. Disabled for the seller (they have optical zoom).
  const viewerZoom = useViewerDigitalZoom(remoteVideoRef, {
    enabled: !isSeller && !!remoteStream,
  });

  return (
    <div className="absolute inset-0 bg-zinc-900 grid place-items-center overflow-hidden">
      {isSeller ? (
        publishing ? (
          <>
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
              data-testid="seller-local-video"
              data-stream-video
              data-stream-role="seller-local"
            />
            {/* Zoom slider removed — pinch-to-zoom on the video surface
                handles all zooming via the `usePinchZoom` hook below. */}
            {/* Desktop only — mobile moves these to the right rail. */}
            <div className="hidden md:flex absolute bottom-3 left-3 gap-2">
              <Button
                onClick={toggleCamera}
                size="sm"
                className="rounded-full bg-zinc-900/80 hover:bg-zinc-800 text-white backdrop-blur"
                data-testid="seller-toggle-camera"
              >
                {cameraOn ? <VideoCameraIcon size={16} /> : <VideoCameraSlashIcon size={16} />}
              </Button>
              <Button
                onClick={toggleMic}
                size="sm"
                className="rounded-full bg-zinc-900/80 hover:bg-zinc-800 text-white backdrop-blur"
                data-testid="seller-toggle-mic"
              >
                {micOn ? "🎤" : "🔇"}
              </Button>
              {/* Flip rear ↔ front camera. On desktop only — mobile uses
                  the right rail bubble. */}
              <Button
                onClick={flipCamera}
                size="sm"
                title={facing === "environment" ? "Switch to selfie camera" : "Switch to rear camera"}
                className="rounded-full bg-zinc-900/80 hover:bg-zinc-800 text-white backdrop-blur"
                data-testid="seller-flip-camera"
              >
                <ArrowsClockwiseIcon size={16} />
              </Button>
              <Button
                onClick={stopPublishing}
                size="sm"
                className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="seller-stop-video"
              >
                End video
              </Button>
            </div>
          </>
        ) : (
          /* Big centered "Go Live" CTA — live-shopping pattern. Pulsing red ring
             draws the seller's eye, single big tap target starts the camera.
             The seller's profile picture sits behind as a clean placeholder. */
          <div
            className="absolute inset-0 grid place-items-center pb-[50%] md:pb-[13%]"
            data-testid="seller-go-live-card"
          >
            {/* Placeholder avatar removed per request. */}
            <div className="relative flex flex-col items-center gap-5 text-zinc-100 p-6 text-center">
              <button
                type="button"
                onClick={startPublishing}
                className="relative h-28 w-28 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white grid place-items-center shadow-2xl shadow-red-600/40 active:scale-95 transition-transform"
                data-testid="seller-start-video"
                aria-label="Go live"
              >
                {/* outer pulse ring */}
                <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" />
                <VideoCameraIcon size={42} weight="fill" className="relative" />
              </button>
              <div className="flex flex-col items-center gap-1">
                <p className="font-display font-black text-2xl tracking-tight">
                  {autoStartFailed ? "Tap to retry camera" : "Tap to go live"}
                </p>
                {autoStartFailed && (
                  <p className="text-xs text-zinc-300/80 max-w-[260px]">
                    Your browser blocked camera access. Allow it in the address bar, then tap above.
                  </p>
                )}
              </div>
            </div>
          </div>
        )
      ) : remoteStream ? (
        <>
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            data-stream-video
            className="h-full w-full object-cover touch-none"
            style={{
              transform: `scale(${viewerZoom})`,
              transformOrigin: "center center",
              transition: viewerZoom === 1 ? "transform 220ms ease-out" : "none",
            }}
            data-testid="viewer-remote-video"
          />
          <div className="absolute bottom-3 left-3 pill pill-dark">MESH · live</div>
          {viewerZoom > 1.01 && (
            <div
              className="absolute top-3 left-1/2 -translate-x-1/2 pill bg-black/65 text-white text-[10px] font-bold border border-white/15 backdrop-blur"
              data-testid="viewer-zoom-pill"
            >
              {viewerZoom.toFixed(1)}× zoom · double-tap to reset
            </div>
          )}
        </>
      ) : (
        /* No overlay during the brief WebRTC handshake. On a LIVE stream
           we paint a branded backdrop (seller picture blurred, or a Bidorah
           radial gradient if the seller has no picture) plus a large
           centered seller mark — so the warm-up state always feels like
           "the show is starting" rather than a dark void. The real video
           covers it the moment the first frame arrives. */
        isLive ? (
          <>
            <div
              className="absolute inset-0"
              data-testid="stream-warmup-backdrop"
              style={{
                background: sellerPicture
                  ? "#0d2436"
                  : "radial-gradient(ellipse at center, #1D78AB 0%, #0F3D5C 55%, #050F1B 100%)",
                backgroundImage: sellerPicture ? `url("${sellerPicture}")` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: sellerPicture ? "blur(28px) brightness(0.72)" : undefined,
                transform: sellerPicture ? "scale(1.08)" : undefined,
              }}
            />
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <SellerAvatar
                name={sellerName || "Seller"}
                picture={sellerPicture}
                size={128}
                editable={false}
              />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 text-zinc-300 p-6 text-center">
            <SellerAvatar
              name={sellerName || "Seller"}
              picture={sellerPicture}
              size={96}
              editable={false}
            />
            <p className="font-display font-bold text-lg">
              {sellerName ? `Waiting for ${sellerName}…` : "Waiting for video…"}
            </p>
            <p className="text-xs text-zinc-400 max-w-[320px]">
              This stream hasn't started. Bookmark it to get notified.
            </p>
          </div>
        )
      )}
    </div>
  );
}
